import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Upload, X, Check, Image, Video, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SubmissionForm = ({ onSuccess, compact = false }) => {
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    text: ''
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('errorName');
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = t('errorEmail');
    }
    
    if (!formData.text.trim()) {
      newErrors.text = t('errorText');
    } else if (formData.text.length > 300) {
      newErrors.text = t('errorTextLength');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error(t('errorFileSize'));
      return;
    }

    // Validate file type
    const isImage = selectedFile.type.startsWith('image/');
    const isVideo = selectedFile.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error(t('errorFileType'));
      return;
    }

    setFile(selectedFile);
    setMediaType(isImage ? 'image' : 'video');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreview(e.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);

    try {
      let uploadedUrl = null;
      let uploadedMediaType = null;

      // Upload file first if exists
      if (file) {
        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const uploadResponse = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrl = uploadResponse.data.url;
        uploadedMediaType = uploadResponse.data.media_type;
        setUploading(false);
      }

      // Submit post
      const postFormData = new FormData();
      postFormData.append('name', formData.name);
      postFormData.append('email', formData.email);
      postFormData.append('text', formData.text);
      if (uploadedUrl) {
        postFormData.append('media_url', uploadedUrl);
        postFormData.append('media_type', uploadedMediaType);
      }

      await axios.post(`${API}/posts`, postFormData);
      
      setSubmitted(true);
      toast.success(t('successTitle'));
      
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(t('errorSubmit'));
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', text: '' });
    setFile(null);
    setFilePreview(null);
    setMediaType(null);
    setSubmitted(false);
    setErrors({});
  };

  const padding = compact ? 'p-4' : 'p-6 sm:p-8';

  if (submitted) {
    return (
      <div className={`${padding} text-center`} data-testid="success-card">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#009639] flex items-center justify-center">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-serif font-semibold text-[#0a0a0a] mb-2">
          {t('successTitle')}
        </h3>
        <p className="text-gray-600 mb-6 text-sm">
          {t('successMessage')}
        </p>
        <Button
          onClick={resetForm}
          className="bg-[#009639] hover:bg-[#007a2f] text-white"
          data-testid="submit-another-btn"
        >
          {t('submitAnother')}
        </Button>
      </div>
    );
  }

  return (
    <div className={padding} data-testid="submission-form">
      {!compact && (
        <h3 className="text-xl font-serif font-semibold text-[#0a0a0a] mb-6">
          {t('formTitle')}
        </h3>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <Input
            type="text"
            placeholder={t('namePlaceholder')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`bg-gray-50 border-gray-200 focus:border-[#009639] focus:ring-[#009639] ${errors.name ? 'border-red-500' : ''}`}
            data-testid="name-input"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <Input
            type="email"
            placeholder={t('emailPlaceholder')}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={`bg-gray-50 border-gray-200 focus:border-[#009639] focus:ring-[#009639] ${errors.email ? 'border-red-500' : ''}`}
            data-testid="email-input"
            dir="ltr"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Text */}
        <div>
          <Textarea
            placeholder={t('textPlaceholder')}
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value.slice(0, 300) })}
            className={`bg-gray-50 border-gray-200 focus:border-[#009639] focus:ring-[#009639] min-h-[100px] resize-none ${errors.text ? 'border-red-500' : ''}`}
            data-testid="text-input"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.text && <p className="text-red-500 text-xs">{errors.text}</p>}
            <p className={`text-xs ${formData.text.length >= 280 ? 'text-[#CE1126]' : 'text-gray-400'} ${language === 'ar' ? 'mr-auto' : 'ml-auto'}`}>
              {formData.text.length}/300
            </p>
          </div>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            {t('uploadLabel')}
          </label>
          
          {!filePreview ? (
            <div
              className={`upload-zone rounded-lg p-4 text-center cursor-pointer transition-all ${dragOver ? 'dragging border-[#009639] bg-[#009639]/5' : 'border-gray-300'}`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              data-testid="upload-zone"
            >
              <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
              <p className="text-gray-600 text-xs">{t('dragDrop')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('uploadHint')}</p>
            </div>
          ) : (
            <div className="relative rounded-lg overflow-hidden bg-gray-100" data-testid="media-preview">
              {mediaType === 'image' ? (
                <img
                  src={filePreview}
                  alt="Preview"
                  className="w-full h-32 object-cover"
                />
              ) : (
                <video
                  src={filePreview}
                  className="w-full h-32 object-cover"
                  controls
                />
              )}
              <button
                type="button"
                onClick={removeFile}
                className="absolute top-2 right-2 bg-[#CE1126] text-white p-1 rounded-full hover:bg-red-700 transition-colors"
                data-testid="remove-file-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                {mediaType === 'image' ? <Image className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                <span className="truncate max-w-[150px]">{file?.name}</span>
              </div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className="hidden"
            data-testid="file-input"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#009639] hover:bg-[#007a2f] text-white py-5 text-base font-medium transition-all relative overflow-hidden group"
          data-testid="submit-btn"
        >
          {/* Red accent on hover */}
          <span className="absolute inset-0 bg-gradient-to-r from-[#009639] via-[#007a2f] to-[#CE1126] opacity-0 group-hover:opacity-100 transition-opacity" />
          <span className="relative">
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {uploading ? 'Uploading...' : t('submitting')}
              </span>
            ) : (
              t('submitButton')
            )}
          </span>
        </Button>
      </form>
    </div>
  );
};
